import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { BrandField, type BrandPickerValue } from '@/app/entities/brands/ui'
import { Button } from '@/common/ui/Form/Button'
import { Fieldset } from '@/common/ui/Form/Fieldset'
import { Input } from '@/common/ui/Form/Input'
import { Select, SelectOption } from '@/common/ui/Form/Select'
import { Textarea } from '@/common/ui/Form/Textarea'
import { MediaSelectCard } from '@/common/ui/MediaSelectCard'
import { useCreateProductForm } from '../model'
import { useNavigate } from 'react-router-dom'

const MODAL_ID = 'create-product-modal'
const CREATE_PRODUCT_BRAND_MODAL_ID = 'create-product-brand-modal'

type ProductCreateFormProps = {
    onBack: () => void
}

export const ProductCreateForm = ({ onBack }: ProductCreateFormProps) => {
    const {
        form,
        handleSubmit,
        onSubmit,
        register,
        isPending,
        isSuccess,
        errors,
        data,
        currentOrgId,
        keyImageFile,
        additionalImagePrimary,
        additionalImageSecondary,
        setKeyImageFile,
        setAdditionalImagePrimary,
        setAdditionalImageSecondary,
    } = useCreateProductForm()
    const [selectedBrand, setSelectedBrand] = useState<BrandPickerValue>(null)
    const navigate = useNavigate()
    const keyImageInputRef = useRef<HTMLInputElement | null>(null)
    const additionalImagesInputRef = useRef<HTMLInputElement | null>(null)
    const additionalImagesAltInputRef = useRef<HTMLInputElement | null>(null)

    const [keyImagePreviewUrl, setKeyImagePreviewUrl] = useState<string | null>(null)
    const [additionalImagePreviewUrl, setAdditionalImagePreviewUrl] = useState<string | null>(null)
    const [additionalImageAltPreviewUrl, setAdditionalImageAltPreviewUrl] = useState<string | null>(null)

    const handleOpenKeyImagePicker = () => {
        keyImageInputRef.current?.click()
    }

    const handleOpenAdditionalImagesPicker = () => {
        additionalImagesInputRef.current?.click()
    }

    const handleOpenAdditionalImagesAltPicker = () => {
        additionalImagesAltInputRef.current?.click()
    }

    const handleKeyImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return

        setKeyImageFile(file)
        form.setValue('key_image', file.name, { shouldValidate: true })
        form.clearErrors('key_image')
    }

    const handleAdditionalImagesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return

        setAdditionalImagePrimary(file)
        form.setValue('additional_images', file.name, { shouldValidate: false })
    }

    const handleAdditionalImagesAltChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return

        setAdditionalImageSecondary(file)
        form.setValue('additional_images', file.name, { shouldValidate: false })
    }

    const handleBrandChange = (brand: BrandPickerValue) => {
        setSelectedBrand(brand)
        form.setValue('brand', brand?.id ?? '', { shouldValidate: true })
    }

    useEffect(() => {
        form.register('key_image', { required: 'Ключевое изображение обязательно' })
        form.register('additional_images')
        form.register('brand')
    }, [form])

    useEffect(() => {
        if (!keyImageFile) {
            setKeyImagePreviewUrl(null)
            return
        }

        const previewUrl = URL.createObjectURL(keyImageFile)
        setKeyImagePreviewUrl(previewUrl)
        return () => {
            URL.revokeObjectURL(previewUrl)
        }
    }, [keyImageFile])

    useEffect(() => {
        if (!additionalImagePrimary) {
            setAdditionalImagePreviewUrl(null)
            return
        }

        const previewUrl = URL.createObjectURL(additionalImagePrimary)
        setAdditionalImagePreviewUrl(previewUrl)
        return () => {
            URL.revokeObjectURL(previewUrl)
        }
    }, [additionalImagePrimary])

    useEffect(() => {
        if (!additionalImageSecondary) {
            setAdditionalImageAltPreviewUrl(null)
            return
        }

        const previewUrl = URL.createObjectURL(additionalImageSecondary)
        setAdditionalImageAltPreviewUrl(previewUrl)
        return () => {
            URL.revokeObjectURL(previewUrl)
        }
    }, [additionalImageSecondary])

    useEffect(() => {
        if (!isSuccess) return

        const checkbox = document.getElementById(MODAL_ID) as HTMLInputElement | null
        if (checkbox) {
            checkbox.checked = false
        }

        if (keyImageInputRef.current) {
            keyImageInputRef.current.value = ''
        }
        if (additionalImagesInputRef.current) {
            additionalImagesInputRef.current.value = ''
        }
        if (additionalImagesAltInputRef.current) {
            additionalImagesAltInputRef.current.value = ''
        }

        setSelectedBrand(null)
    }, [isSuccess])

    useEffect(() => {
        if (!isSuccess || !data?.id || !currentOrgId) return

        navigate(`/app/products/${data.id}`)
    }, [currentOrgId, data?.id, isSuccess, navigate])

    return (
        <form className='flex flex-col gap-6' onSubmit={handleSubmit(onSubmit)}>
            <div>
                <h2 className='text-2xl font-medium text-white'>Новый товар</h2>
                <p className='mt-2 text-base text-[var(--gray/light-middle,#aba7af)]'>
                    Заполните все обязательные поля, чтобы продолжить
                </p>
            </div>

            <div className='flex flex-col gap-6 lg:flex-row'>
                <div className='flex flex-col gap-5 w-full lg:w-2/3'>
                    <Fieldset label='Маркетплейс' required errorText={errors.marketplace?.message}>
                        <Select
                            size='lg'
                            className='w-full'
                            placeholder='Выберите площадку'
                            {...register('marketplace', { required: 'Маркетплейс обязателен' })}
                        >
                            <SelectOption value='wildberries'>Wildberries</SelectOption>
                            <SelectOption value='ozon'>Ozon</SelectOption>
                        </Select>
                    </Fieldset>

                    <Fieldset label='Бренд' errorText={errors.brand?.message}>
                        <BrandField
                            modalId={CREATE_PRODUCT_BRAND_MODAL_ID}
                            value={selectedBrand}
                            onChange={handleBrandChange}
                        />
                    </Fieldset>

                    <Fieldset label='Название' required errorText={errors.title?.message}>
                        <Input
                            size='lg'
                            className='w-full'
                            placeholder='Мягкий чехол для MacBook Air 14 дюймов'
                            type='text'
                            {...register('title', { required: 'Название товара обязательно' })}
                        />
                    </Fieldset>

                    <Fieldset label='Описание' required errorText={errors.description?.message}>
                        <Textarea
                            size='lg'
                            className='w-full min-h-[120px]'
                            placeholder='Тонкий чехол для макбука – это идеальное сочетание стиля и надежной защиты для вашего ноутбука.'
                            {...register('description', { required: 'Описание товара обязательно' })}
                        />
                    </Fieldset>
                </div>

                <div className='flex flex-col gap-5 w-full lg:w-1/3'>
                    <Fieldset label='Ключевое изображение' required errorText={errors.key_image?.message}>
                        <div className='flex flex-col gap-2'>
                            <MediaSelectCard
                                imageUrl={keyImagePreviewUrl}
                                title='Товар целиком'
                                description='Однотонный фон, крупный план, хорошее качество'
                                className='bg-[rgba(255,255,255,0.15)]'
                                placeholderVariant='blank'
                                placeholderClassName='rounded-[8px] bg-white'
                                imageClassName='rounded-[8px]'
                                titleClassName='text-[color:var(--gray/light,#d9d9d9)]'
                                descriptionClassName='text-[var(--gray/light-middle,#aba7af)]'
                                ariaLabel='Загрузить ключевое изображение'
                                onClick={handleOpenKeyImagePicker}
                            />
                            <input
                                id='key-image-input'
                                type='file'
                                accept='image/*'
                                className='hidden'
                                onChange={handleKeyImageChange}
                                ref={element => {
                                    keyImageInputRef.current = element
                                }}
                            />
                            <p className='text-sm text-[var(--gray/light-middle,#aba7af)]'>
                                Будет использоваться по-умолчанию для ваших будущих генераций.
                            </p>
                        </div>
                    </Fieldset>

                    <Fieldset label='Дополнительные изображения' errorText={errors.additional_images?.message}>
                        <div className='flex flex-col gap-2'>
                            <div className='flex flex-col gap-3'>
                                <MediaSelectCard
                                    imageUrl={additionalImagePreviewUrl}
                                    title='Товар в упаковке'
                                    description='Для презентабельности'
                                    className='bg-[rgba(255,255,255,0.02)]'
                                    titleClassName='text-[color:var(--gray/light,#d9d9d9)]'
                                    descriptionClassName='text-[var(--gray/light-middle,#aba7af)]'
                                    ariaLabel='Добавить изображение товара в упаковке'
                                    onClick={handleOpenAdditionalImagesPicker}
                                />
                                <MediaSelectCard
                                    imageUrl={additionalImageAltPreviewUrl}
                                    title='Товар в другом цвете'
                                    description='Для кросс-продаж'
                                    className='bg-[rgba(255,255,255,0.02)]'
                                    titleClassName='text-[color:var(--gray/light,#d9d9d9)]'
                                    descriptionClassName='text-[var(--gray/light-middle,#aba7af)]'
                                    ariaLabel='Добавить изображение товара в другом цвете'
                                    onClick={handleOpenAdditionalImagesAltPicker}
                                />
                            </div>
                            <input
                                id='additional-images-input'
                                type='file'
                                accept='image/*'
                                className='hidden'
                                onChange={handleAdditionalImagesChange}
                                ref={element => {
                                    additionalImagesInputRef.current = element
                                }}
                            />
                            <input
                                id='additional-images-alt-input'
                                type='file'
                                accept='image/*'
                                className='hidden'
                                onChange={handleAdditionalImagesAltChange}
                                ref={element => {
                                    additionalImagesAltInputRef.current = element
                                }}
                            />
                            <p className='text-sm text-[var(--gray/light-middle,#aba7af)]'>
                                Вы можете загрузить их сейчас, или позже, на этапе генерации.
                            </p>
                        </div>
                    </Fieldset>
                </div>
            </div>

            {errors.root?.message && <div className='text-sm text-error'>{errors.root?.message}</div>}

            <div className='flex items-center justify-end gap-2'>
                <Button emphasis='secondary' variant='ghost' size='lg' onClick={onBack}>
                    Назад
                </Button>
                <Button
                    type='submit'
                    size='lg'
                    emphasis='accent'
                    disabled={isPending || !form.formState.isValid}
                    loading={isPending}
                >
                    Добавить
                </Button>
            </div>
        </form>
    )
}
